import type { RefreshOutcome } from "./types";

/**
 * Rafraîchissement PROACTIF : renouveler le jeton un peu avant son expiration
 * plutôt que de subir un 401.
 *
 * Sans ça, toute reprise après une heure d'inactivité coûte un aller-retour raté
 * (visible : skeleton, toast d'erreur sur une mutation) avant que le rejeu ne
 * répare. Trois déclencheurs, car aucun ne suffit seul :
 *   - un timer, pour l'onglet resté au premier plan ;
 *   - `visibilitychange`, parce que les navigateurs throttlent voire gèlent les
 *     timers des onglets en arrière-plan (et qu'une machine en veille les rate) ;
 *   - `online`, pour reprendre dès le retour du réseau.
 */

/** Marge avant expiration à laquelle on déclenche le renouvellement. */
const REFRESH_LEAD_MS = 5 * 60 * 1000;
/** Palier après un échec transitoire — évite de marteler un serveur qui tousse. */
const RETRY_AFTER_UNAVAILABLE_MS = 60 * 1000;
/** `setTimeout` sature au-delà de 2^31-1 ms et se déclenche alors immédiatement. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

export interface ProactiveRefreshOptions {
  readSessionExpiry: () => number | null;
  tryRefreshSession: () => Promise<RefreshOutcome>;
  /** Session réellement finie — l'app doit rediriger. */
  onSessionLost?: () => void;
}

/** Démarre la boucle. Retourne la fonction d'arrêt. */
export function startProactiveRefresh(options: ProactiveRefreshOptions): () => void {
  const { readSessionExpiry, tryRefreshSession, onSessionLost } = options;

  if (typeof window === "undefined") return () => {};

  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let stopped = false;

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = () => {
    clear();
    if (stopped) return;
    const expiry = readSessionExpiry();
    // Pas de hint = pas de session (jamais connecté, ou déconnecté) : rien à
    // renouveler. Le prochain login reposera le cookie et relancera la boucle.
    if (expiry === null) return;
    const delay = Math.min(Math.max(expiry - Date.now() - REFRESH_LEAD_MS, 0), MAX_TIMEOUT_MS);
    timer = setTimeout(() => void run(), delay);
  };

  const run = async () => {
    if (stopped || running) return;
    running = true;
    try {
      const outcome = await tryRefreshSession();
      if (stopped) return;
      if (outcome === "unauthenticated") {
        clear();
        onSessionLost?.();
        return;
      }
      if (outcome === "unavailable") {
        clear();
        timer = setTimeout(() => void run(), RETRY_AFTER_UNAVAILABLE_MS);
        return;
      }
      // "refreshed" : le hint porte une nouvelle expiration, on se reprogramme
      // dessus. `tryRefreshSession` passe par le verrou inter-onglets, donc N
      // onglets qui arrivent à échéance en même temps ne déclenchent qu'un seul
      // appel serveur — sans quoi le timer recréerait la course de rotation que
      // le verrou est censé supprimer.
      schedule();
    } finally {
      running = false;
    }
  };

  /** Réveil (retour d'onglet, retour réseau) : rattraper si l'échéance approche. */
  const runIfDue = () => {
    if (stopped) return;
    const expiry = readSessionExpiry();
    if (expiry === null) return;
    if (expiry - Date.now() <= REFRESH_LEAD_MS) {
      void run();
    } else {
      schedule();
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") runIfDue();
  };

  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("online", runIfDue);
  schedule();

  return () => {
    stopped = true;
    clear();
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("online", runIfDue);
  };
}
